import { formatDate } from 'helpers/date';
import * as Rx from 'rxjs';
import { debounceTime, map, switchMap, take, tap } from 'rxjs/operators';
import type { ICancelFilter } from 'v2/interfaces/reports/cancel';
import { CustomerSearchTypes, InterruptionTabs } from 'v2/interfaces/reports/interruption';
import type { InterruptionChart } from 'v2/interfaces/reports/interruption';

import apiService from '../api';
import type { ApiService } from '../api';

const today = formatDate(new Date(), 'dd/MM/yyyy');

export const initialFilter: ICancelFilter = {
  id: '',
  date: [today, today],
  customer_type: CustomerSearchTypes.EMAIL,
  customer: '',
  product_id: [],
  invoice_id: '',
  reason_id: []
};

class CancelService {
  private filters = new Rx.BehaviorSubject<ICancelFilter>(initialFilter);
  private loading = new Rx.BehaviorSubject<boolean>(true);
  private chart = new Rx.BehaviorSubject<InterruptionChart>(null);
  private statistics = new Rx.BehaviorSubject<any>(null);

  constructor(private apiServices: ApiService) {}

  public setFilter(filter: ICancelFilter): void {
    this.loading.next(true);
    this.filters.pipe(take(1)).subscribe((currentFilter: ICancelFilter) => {
      this.filters.next({ ...currentFilter, ...filter });
    });
  }

  public isLoading = () => this.loading;
  public getFilters = (): Rx.Observable<ICancelFilter> => this.filters;
  public getChart = (): Rx.Observable<InterruptionChart> => this.chart;
  public getStatistics = (): Rx.Observable<any[]> => this.statistics;

  public formatFilter = (filter: ICancelFilter): Partial<ICancelFilter> => {
    const [start, end] = filter.date;
    return {
      canceled_at_initial: start,
      canceled_at_final: end,
      id: filter.id || undefined,
      product_id: filter.product_id.length > 0 ? filter.product_id : undefined,
      invoice_id: filter.invoice_id || undefined,
      reason_id: filter.reason_id.length > 0 ? filter.reason_id : undefined,
      [filter.customer_type]: filter.customer ? filter.customer : undefined
    };
  };

  public loadData(tab: string) {
    return this.getFilters().pipe(
      map(filter => this.formatFilter(filter)),
      debounceTime(10),
      switchMap(filter => {
        this.loading.next(true);
        return this.apiServices.get('reports/subscriptions/cancellations/chart', filter);
      }),
      tap(response => {
        if (tab === InterruptionTabs.CANCEL) {
          this.chart.next(response);
        } else {
          this.statistics.next(response);
        }

        this.loading.next(false);
      })
    );
  }

  public export() {
    return this.apiServices.get('reports/subscriptions/cancellations/export', this.formatFilter(this.filters.value));
  }
}

export const cancelService = new CancelService(apiService);
